import React from 'react';
import { CustomPaymentModal } from '../../pages/registration/custom-payment-modal';
import { useUserContext } from '../../hooks/userContext';
import { DesignerSubscriptionType } from '../../__generated__/types';
import { getDesignerSmartGuildBillingSplit } from '../../utils/designer-smart-guild-pricing';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (response: any) => void;
  onFailure?: (errorMessage: string) => void;
  userType: 'designer' | 'homeowner';
  membershipTier?: DesignerSubscriptionType | string;
  selectedPlanId?: string;
  planData?: any;
  amount?: number;
  planName?: string;
  billingCycle?: string;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onFailure,
  userType,
  membershipTier,
  selectedPlanId,
  planData,
  amount,
  planName = '',
  billingCycle = 'monthly',
}) => {
  const { user } = useUserContext();

  // Handle payment success
  const handlePaymentSuccess = (response: any) => {
    onSuccess(response);
  };

  // Handle payment failure
  const handlePaymentFailure = (errorMessage: string) => {
    onFailure?.(errorMessage);
  };

  // Get customer info from user context
  // Note: UserFromServer doesn't have address fields, so we provide defaults
  const customerInfo = {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    address: '', // Not available in UserFromServer
    city: '', // Not available in UserFromServer
    state: '', // Not available in UserFromServer
    zipCode: '', // Not available in UserFromServer
    phoneNumber: '', // Not available in UserFromServer
  };

  // Determine plan type based on user type and membership tier
  const getPlanType = () => {
    if (userType === 'designer') {
      switch (membershipTier) {
        case DesignerSubscriptionType.Home:
        case DesignerSubscriptionType.HomeBeta:
        case DesignerSubscriptionType.HomeMonthly:
        case DesignerSubscriptionType.HomeMonthlyBeta:
        case DesignerSubscriptionType.HomeBiAnnually:
        case DesignerSubscriptionType.HomeBiAnnuallyBeta:
          return 'Home';
        case DesignerSubscriptionType.Design:
        case DesignerSubscriptionType.DesignBeta:
        case DesignerSubscriptionType.DesignMonthly:
        case DesignerSubscriptionType.DesignMonthlyBeta:
        case DesignerSubscriptionType.DesignBiAnnually:
        case DesignerSubscriptionType.DesignBiAnnuallyBeta:
          return 'Design';
        case DesignerSubscriptionType.DesignPlus:
        case DesignerSubscriptionType.DesignPlusBeta:
        case DesignerSubscriptionType.DesignPlusMonthly:
        case DesignerSubscriptionType.DesignPlusMonthlyBeta:
        case DesignerSubscriptionType.DesignPlusBiAnnually:
        case DesignerSubscriptionType.DesignPlusBiAnnuallyBeta:
          return 'Design+';
        case DesignerSubscriptionType.Guild:
        case DesignerSubscriptionType.GuildBeta:
        case DesignerSubscriptionType.GuildBiAnnually:
        case DesignerSubscriptionType.GuildBiAnnuallyBeta:
          return 'Guild';
        case DesignerSubscriptionType.GuildPlus:
        case DesignerSubscriptionType.GuildPlusBeta:
          return 'Guild+';
        default:
          return 'Design';
      }
    } else {
      // Homeowner logic
      return membershipTier as string;
    }
  };

  const planType = getPlanType();
  const finalPlanName = planName || planType;
  const designerSmartGuildSplit =
    userType === "designer" ? getDesignerSmartGuildBillingSplit(planData) : null;
  const finalAmount =
    amount ??
    (userType === "designer"
      ? Number(
          designerSmartGuildSplit?.dueNowAmount ??
            planData?.metadata?.dueNowAmount ??
            planData?.pricing?.price ??
            0
        )
      : Number(planData?.pricing?.price ?? 0));
  const finalBillingCycle =
    billingCycle ||
    planData?.pricing?.period ||
    'monthly';

  return (
    <CustomPaymentModal
      open={isOpen}
      onClose={onClose}
      onPaymentSuccess={handlePaymentSuccess}
      onPaymentFailure={handlePaymentFailure}
      amount={finalAmount}
      planName={finalPlanName}
      planType={planType}
      billingCycle={finalBillingCycle}
      email={user?.email || ''}
      homeownerId={user?.id || ''}
      customerInfo={customerInfo}
      planData={planData}
    />
  );
};
